import Image from "next/image";
import Link from "next/link";
import { type ImageSummary } from "../types";
import { formatPriceRange } from "@/lib/utils";
import { MAX_FULFILLABLE_CENTS } from "@/lib/variants";

// Image path: https://<cdn>/static/images/products/<img>/<img>-<orientation>-no-frame-no-mat.webp
export function ProductElement({
	product,
	loading,
	priority,
}: {
	product: ImageSummary;
	loading: "eager" | "lazy";
	priority?: boolean;
}) {
	// ASSET: square-ish crystal on bone, consistent framing across the grid (source is the
	// per-orientation product image; a dedicated 1000×1000 gallery crop is ideal later).
	const imageUrl = `https://d127u13u6yy1k7.cloudfront.net/static/images/products/${product.img_name}/${product.img_name}-${product.orientation}-no-frame-no-mat.webp`;
	return (
		// Cardless (DESIGN.md §4.2): image in negative space, quiet caption below — no border/shadow.
		<li data-testid="ProductElement" className="group">
			<Link href={`/shop/${product.img_name}`} key={product.img_name} className="block">
				<div className="relative aspect-square overflow-hidden bg-bone-deep">
					<Image
						loading={loading}
						src={imageUrl}
						alt={`${product.title} — crystallized ${product.substance}`}
						fill
						sizes={"(min-width: 1024px) 28rem, (min-width: 640px) 45vw, 100vw"}
						priority={priority}
						className="object-cover transition-transform duration-700 ease-gallery group-hover:scale-[1.03]"
					/>
					{product.has_limited ? (
						<span className="label-caps bg-bone/85 absolute left-3 top-3 px-2 py-1 text-mg-gold-deep">
							Limited
						</span>
					) : null}
				</div>
				<div className="mt-4 flex items-baseline justify-between gap-4">
					<h3 className="font-serif text-lg font-medium text-mg-black">{product.title}</h3>
					<p className="whitespace-nowrap text-sm text-ink-muted" data-testid="ProductElement_PriceRange">
						{formatPriceRange(product.price_range, "Coming soon", MAX_FULFILLABLE_CENTS)}
					</p>
				</div>
				<p className="label-caps mt-1 text-ink-faint" data-testid="ProductElement_Series">
					Crystallized {product.substance}
				</p>
			</Link>
		</li>
	);
}
